import Drawer from '@/components/Drawer';
import { useStoreWatch } from '@/components/Form/useStoreWatch';
import { useSubmitOnEnter } from '@/components/Form/useSubmitOnEnter';
import { EMAIL_CENTER_ACTIONS } from '@/constants/actions';
import * as EmailCenterSelectors from '@/selectors/emailCenterSelectors';
import { patchJourneyEmailConfigRequest } from '@/services/api/emailCenter';
import { applyServerErrorsToForm, normalizeApiError } from '@/utils/apiErrors';
import { F, getIntlTranslation } from '@/utils/intl';
import { processAPIResponseNow } from '@/utils/request';
import {
  Alert,
  Button,
  Collapse,
  Form,
  Input,
  message,
  Select,
  Space,
  Switch,
} from 'antd';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useIntl, useSelector } from 'umi';
import { REPLY_TO_MAX } from '../../constants';
import { objectToRows, rowsToObject } from '../../keyValue';

import { useEmailCenterValidateMessages } from '../../useValidateMessages';
import styles from './JourneyEmailsTab.less';

const BOOKING_SENDER_MODES = ['agent', 'custom', 'static'] as const;

const EmailConfigDrawer: React.FC = () => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const validateMessages = useEmailCenterValidateMessages();
  const [form] = Form.useForm();

  const drawer = useSelector(EmailCenterSelectors.selectDrawer('emailConfig'));
  const config = useSelector(EmailCenterSelectors.selectJourneyEmailConfig);
  const journeyHashId = useSelector(
    EmailCenterSelectors.selectSelectedJourneyHashId,
  );

  const [saving, setSaving] = useState(false);
  const [generalError, setGeneralError] = useState<string | null>(null);

  const bookingSenderMode = Form.useWatch(['booking_sender', 'mode'], form);
  // Read the store, not the mounted fields: both sections sit in collapsed
  // panels, whose controls are unmounted until the panel is opened.
  const utmOverride = useStoreWatch<boolean>('utm_override', form);
  const fromName = useStoreWatch<string | undefined>('from_name', form);
  const fromEmail = useStoreWatch<string | undefined>('from_email', form);

  useEffect(() => {
    if (!drawer.visible) return;
    setGeneralError(null);
    form.setFieldsValue({
      from_name: config?.from_name,
      from_email: config?.from_email,
      reply_to: config?.reply_to || [],
      utm_campaign: config?.utm_campaign,
      utm_source: config?.utm_source,
      utm_medium: config?.utm_medium,
      // The API has no "uses custom UTM parameters" flag — carrying any of the
      // three is what it means, so that is what the toggle is restored from.
      utm_override: Boolean(
        config?.utm_campaign || config?.utm_source || config?.utm_medium,
      ),
      booking_sender: config?.booking_sender || { mode: 'agent' },
      template_vars: objectToRows(config?.template_vars),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawer.visible]);

  const close = useCallback(() => {
    dispatch({
      type: EMAIL_CENTER_ACTIONS.CLOSE_DRAWER,
      payload: { drawer: 'emailConfig' },
    });
  }, [dispatch]);

  const handleSave = useCallback(async () => {
    if (!journeyHashId) return;
    await form.validateFields();

    // The store, not `validateFields`' result: that one returns only mounted
    // fields, so a collapsed section — or a UTM field the toggle just cleared
    // and unmounted — would drop out of the payload, and with it the clearing.
    const values = { ...(form.getFieldsValue(true) as Record<string, any>) };
    delete values.utm_override; // UI-only opt-in, not a stored config key.

    // PATCH deep-merges server-side, so untouched stored keys survive.
    // Consequence: removing a template_var here does not delete it remotely —
    // clearing its value is the supported way to disable one.
    const payload = {
      ...values,
      template_vars: rowsToObject(values.template_vars),
    };

    setSaving(true);
    setGeneralError(null);
    const result = await patchJourneyEmailConfigRequest({
      journeyHashId,
      payload,
    });
    setSaving(false);

    // The save can land here and still leave the journey unable to send: this
    // write is also what creates the journey's Mailcoach mailing list, and
    // `provider_synced: false` is the backend saying it could not. A plain
    // "saved" is what made that state invisible — the next step (adding an
    // email) then looks like it worked and never sends.
    const providerSynced =
      (result as { data?: { provider_synced?: boolean } } | undefined)?.data
        ?.provider_synced !== false;

    processAPIResponseNow(result, {
      showSuccessMessage: providerSynced,
      // Conditional, not just `showSuccessMessage`: the helper shows the
      // success toast for a `successMessage` alone, so leaving it set would put
      // "saved" on screen next to the warning that says it did not finish.
      successMessage: providerSynced
        ? getIntlTranslation(intl, {
            id: 'pages.emailCenter.journeys.sender.saved',
            defaultMessage: 'Sender settings saved.',
          })
        : undefined,
      onSuccess: () => {
        if (!providerSynced) {
          message.warning({
            content: getIntlTranslation(intl, {
              id: 'pages.emailCenter.journeys.sender.savedWithoutMailingList',
              defaultMessage:
                'Sender settings saved, but the mailing list could not be created in Mailcoach — this journey cannot send until it exists. Saving again retries it.',
            }),
            key: 'email_config_mailing_list',
            duration: 10,
          });
        }
        close();
        dispatch({
          type: EMAIL_CENTER_ACTIONS.FETCH_JOURNEY_EMAIL_CONFIG,
          payload: { journeyHashId },
        });
      },
      onError: (data) => {
        const normalized = normalizeApiError(data);
        applyServerErrorsToForm(form, normalized.fieldErrors, {
          onUnmapped: (key, messages) =>
            setGeneralError((prev) => prev ?? `${key}: ${messages.join(' ')}`),
        });
        if (Object.keys(normalized.fieldErrors).length === 0) {
          setGeneralError(normalized.message);
        }
      },
    });
  }, [journeyHashId, form, intl, close, dispatch]);

  const submitOnEnter = useSubmitOnEnter(handleSave, { enabled: !saving });

  return (
    <Drawer
      width={560}
      open={drawer.visible}
      onOpenChange={close}
      title={
        <F
          id="pages.emailCenter.journeys.sender.drawerTitle"
          defaultMessage="Sender & tracking"
        />
      }
      footer={
        <Space className={styles.drawerFooter}>
          <Button onClick={close}>
            <F id="common.actions.cancel" defaultMessage="Cancel" />
          </Button>
          <Button type="primary" loading={saving} onClick={handleSave}>
            <F id="common.actions.save" defaultMessage="Save" />
          </Button>
        </Space>
      }
    >
      {generalError && (
        <Alert
          className={styles.generalError}
          type="error"
          showIcon
          message={generalError}
        />
      )}

      <Form
        validateMessages={validateMessages}
        form={form}
        layout="vertical"
        {...submitOnEnter}
      >
        <Form.Item
          name="from_name"
          label={
            <F
              id="pages.emailCenter.journeys.sender.fromName"
              defaultMessage="From name"
            />
          }
          rules={[{ required: true }]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          name="from_email"
          label={
            <F
              id="pages.emailCenter.journeys.sender.fromEmail"
              defaultMessage="From email"
            />
          }
          rules={[{ required: true }, { type: 'email' }]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          name="reply_to"
          label={
            <F
              id="pages.emailCenter.journeys.sender.replyTo"
              defaultMessage="Reply-to"
            />
          }
          rules={[
            {
              validator: (_, value: string[] = []) => {
                if (value.length > REPLY_TO_MAX) {
                  return Promise.reject(
                    getIntlTranslation(intl, {
                      id: 'pages.emailCenter.journeys.sender.replyToMax',
                      defaultMessage: 'At most {max} reply-to addresses.',
                      values: { max: REPLY_TO_MAX },
                    }),
                  );
                }
                const invalid = value.find(
                  (entry) => !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(entry),
                );
                return invalid
                  ? Promise.reject(
                      getIntlTranslation(intl, {
                        id: 'pages.emailCenter.journeys.sender.replyToInvalid',
                        defaultMessage:
                          '"{email}" is not a valid email address.',
                        values: { email: invalid },
                      }),
                    )
                  : Promise.resolve();
              },
            },
          ]}
        >
          <Select
            mode="tags"
            open={false}
            suffixIcon={null}
            tokenSeparators={[',', ' ']}
          />
        </Form.Item>

        <Collapse
          ghost
          items={[
            {
              key: 'tracking',
              label: (
                <F
                  id="pages.emailCenter.journeys.sender.trackingSection"
                  defaultMessage="UTM tracking"
                />
              ),
              children: (
                <>
                  <Form.Item
                    name="utm_override"
                    label={
                      <F
                        id="pages.emailCenter.journeys.sender.utmCustom"
                        defaultMessage="Custom UTM parameters"
                      />
                    }
                    tooltip={
                      <F
                        id="pages.emailCenter.journeys.sender.utmAuto"
                        defaultMessage="UTM Parameters are automatically set by Conversional based on the email"
                      />
                    }
                    valuePropName="checked"
                  >
                    <Switch
                      onChange={(checked) => {
                        // Emptied, not left standing: the fields unmount here,
                        // but PATCH merges, so a stored value survives unless
                        // this save clears it.
                        if (!checked) {
                          form.setFieldsValue({
                            utm_campaign: '',
                            utm_source: '',
                            utm_medium: '',
                          });
                        }
                      }}
                    />
                  </Form.Item>
                  {utmOverride && (
                    <>
                      <Form.Item
                        name="utm_campaign"
                        label="utm_campaign"
                        rules={[{ max: 100 }]}
                      >
                        <Input />
                      </Form.Item>
                      <Form.Item
                        name="utm_source"
                        label="utm_source"
                        rules={[{ max: 100 }]}
                      >
                        <Input />
                      </Form.Item>
                      <Form.Item
                        name="utm_medium"
                        label="utm_medium"
                        rules={[{ max: 100 }]}
                      >
                        <Input />
                      </Form.Item>
                    </>
                  )}
                </>
              ),
            },
            {
              key: 'bookingSender',
              label: (
                <F
                  id="pages.emailCenter.journeys.sender.bookingSection"
                  defaultMessage="Booking sender"
                />
              ),
              children: (
                <>
                  <Form.Item
                    name={['booking_sender', 'mode']}
                    label={
                      <F
                        id="pages.emailCenter.journeys.sender.bookingMode"
                        defaultMessage="Mode"
                      />
                    }
                  >
                    <Select
                      options={BOOKING_SENDER_MODES.map((mode) => ({
                        value: mode,
                        label: getIntlTranslation(intl, {
                          id: `pages.emailCenter.journeys.sender.bookingMode.${mode}`,
                          defaultMessage: mode,
                        }),
                      }))}
                    />
                  </Form.Item>
                  {bookingSenderMode === 'static' && (
                    <>
                      {/* `static` resolves to the journey's own from_name /
                          from_email, so these edit those very fields — the mode
                          is picked for lead sources that have no sender yet. */}
                      <Alert
                        className={styles.bookingSenderHint}
                        type="info"
                        showIcon
                        message={
                          <F
                            id="pages.emailCenter.journeys.sender.bookingStaticHint"
                            defaultMessage="Booking emails go out as the journey's own sender. Editing it here changes it for every email this lead source sends."
                          />
                        }
                      />
                      <Form.Item
                        label={
                          <F
                            id="pages.emailCenter.journeys.sender.fromName"
                            defaultMessage="From name"
                          />
                        }
                        required
                      >
                        <Input
                          value={fromName}
                          onChange={(event) =>
                            form.setFieldsValue({
                              from_name: event.target.value,
                            })
                          }
                        />
                      </Form.Item>
                      <Form.Item
                        label={
                          <F
                            id="pages.emailCenter.journeys.sender.fromEmail"
                            defaultMessage="From email"
                          />
                        }
                        required
                      >
                        <Input
                          value={fromEmail}
                          onChange={(event) =>
                            form.setFieldsValue({
                              from_email: event.target.value,
                            })
                          }
                        />
                      </Form.Item>
                    </>
                  )}
                  {bookingSenderMode === 'custom' && (
                    <>
                      <Form.Item
                        name={['booking_sender', 'name']}
                        label={
                          <F id="common.attribute.name" defaultMessage="Name" />
                        }
                        rules={[{ required: true }]}
                      >
                        <Input />
                      </Form.Item>
                      <Form.Item
                        name={['booking_sender', 'email']}
                        label={
                          <F
                            id="common.attribute.email"
                            defaultMessage="Email"
                          />
                        }
                        rules={[{ required: true }, { type: 'email' }]}
                      >
                        <Input />
                      </Form.Item>
                    </>
                  )}
                </>
              ),
            },
          ]}
        />
      </Form>
    </Drawer>
  );
};

export default EmailConfigDrawer;
